/**
 * Become a courier — the rules the page applies, kept apart from React so each
 * can be pinned by a plain test.
 *
 * Everything here is either the frame's own wording or the platform's own rule
 * (`logistics/courier/src/domain/courier.ts`). Where the two disagree the
 * platform wins, because the frame is a prototype and the platform is what a
 * courier's application is actually decided against.
 */

/** The four vehicles the platform accepts from a courier who signs themselves up. */
export type PlatformVehicle = 'CAR' | 'SCOOTER' | 'VAN' | 'E_BIKE';

export interface VehicleChoice {
  /** The frame's own key, kept so the picker reads like the frame. */
  readonly id: 'bike' | 'ebike' | 'moped' | 'car' | 'van';
  readonly label: string;
  /** The platform's value. Absent means the platform does not offer it. */
  readonly vehicle?: PlatformVehicle;
  readonly icon: string;
}

/**
 * The frame's vehicles, mapped onto the platform's (owner decision D12, its
 * recommendation, 2026-11-01):
 *
 * - **Bicycle** — the platform has no pedal bicycle for a new courier, and
 *   adding one is the owner's call, so it is drawn and marked "Coming soon".
 * - **E-bike** → `E_BIKE`; **Moped** → `SCOOTER` (the platform's motorbike or
 *   moped); **Car** → `CAR`.
 * - **Van** is not in the frame but is in the platform, so it is offered too.
 */
export const VEHICLE_CHOICES: readonly VehicleChoice[] = [
  {
    id: 'bike',
    label: 'Bicycle',
    icon: 'M5.5 17a3 3 0 1 0 0-.01M18.5 17a3 3 0 1 0 0-.01M5.5 17 9 9h5l4.5 8M9 9 8 6.5H6M14 9l-2.5 8',
  },
  {
    id: 'ebike',
    label: 'E-bike',
    vehicle: 'E_BIKE',
    icon: 'M5.5 17a3 3 0 1 0 0-.01M18.5 17a3 3 0 1 0 0-.01M5.5 17 9 9h5l4.5 8M13 4l-2 3h3l-2 3',
  },
  {
    id: 'moped',
    label: 'Moped',
    vehicle: 'SCOOTER',
    icon: 'M5.5 17a2.5 2.5 0 1 0 0-.01M18.5 17a2.5 2.5 0 1 0 0-.01M8 17h8l2-6h-4l-2 3H6M16 8h3',
  },
  {
    id: 'car',
    label: 'Car',
    vehicle: 'CAR',
    icon: 'M4 16v-4l2-5h12l2 5v4ZM4 12h16M7 16v2M17 16v2',
  },
  {
    id: 'van',
    label: 'Van',
    vehicle: 'VAN',
    icon: 'M3 16V7h11v9M14 10h4l3 3v3h-7M3 16h18M7 18.5a1.5 1.5 0 1 0 0-.01M17 18.5a1.5 1.5 0 1 0 0-.01',
  },
];

/** How the platform names each vehicle back to the courier. */
export function vehicleLabel(vehicle: string): string {
  switch (vehicle) {
    case 'E_BIKE':
      return 'E-bike';
    case 'SCOOTER':
      return 'Moped or motorbike';
    case 'CAR':
      return 'Car';
    case 'VAN':
      return 'Van';
    case 'BICYCLE':
      return 'Bicycle';
    case 'ON_FOOT':
      return 'On foot';
    default:
      return vehicle;
  }
}

/** The platform's rule: a motor vehicle brings a driving licence and a registration number. */
export function needsLicence(vehicle: PlatformVehicle): boolean {
  return vehicle !== 'E_BIKE';
}

export interface CourierForm {
  readonly first: string;
  readonly last: string;
  readonly email: string;
  readonly phone: string;
  readonly postcode: string;
  readonly vehicle: PlatformVehicle | undefined;
  readonly over18: boolean;
}

export type CourierFormErrors = Partial<
  Record<'first' | 'last' | 'email' | 'phone' | 'pc' | 'veh' | 'age', string>
>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const POSTCODE = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/;

/** The frame's own checks and messages. */
export function validateForm(form: CourierForm): CourierFormErrors {
  const errors: { -readonly [K in keyof CourierFormErrors]: string } = {};
  if (form.first.trim() === '') errors.first = 'Enter your first name';
  if (form.last.trim() === '') errors.last = 'Enter your last name';
  if (!EMAIL.test(form.email.trim())) errors.email = 'Enter a valid email';
  if (ukMobile(form.phone) === undefined) errors.phone = 'Enter a UK mobile number';
  if (!POSTCODE.test(form.postcode.trim())) errors.pc = 'Enter a valid UK postcode';
  if (form.vehicle === undefined) errors.veh = "Choose how you'll deliver";
  if (!form.over18) errors.age = 'You must confirm this to continue';
  return errors;
}

/**
 * A UK mobile number in the form the platform stores (+447…), or undefined.
 *
 * The frame only counted ten digits; the platform's contact points take an
 * E.164 number, so a number that is not a UK mobile is refused here, in words,
 * rather than by the server later.
 */
export function ukMobile(input: string): string | undefined {
  const digits = input.replace(/[\s()-]/g, '');
  const national = digits.startsWith('+44')
    ? `0${digits.slice(3)}`
    : digits.startsWith('0044')
      ? `0${digits.slice(4)}`
      : digits;
  if (!/^07\d{9}$/.test(national)) return undefined;
  return `+44${national.slice(1)}`;
}

/** A postcode in the form the platform writes it: upper case, one space before the inward code. */
export function tidyPostcode(input: string): string {
  const compact = input.replace(/\s+/g, '').toUpperCase();
  return compact.length > 3 ? `${compact.slice(0, -3)} ${compact.slice(-3)}` : compact;
}

/**
 * The name customers see: first name and the initial of the last, which is the
 * platform's convention for a courier (`Courier.displayName`). A courier's full
 * name is never shown to the person they deliver to.
 */
export function courierDisplayName(first: string, last: string): string {
  const given = first.trim().replace(/\s+/g, ' ');
  const initial = last.trim().charAt(0).toUpperCase();
  return (initial === '' ? given : `${given} ${initial}.`).slice(0, 60);
}

/** A vehicle registration as the platform keeps it: upper case, no spaces. */
export function tidyRegistration(input: string): string {
  return input.replace(/\s+/g, '').toUpperCase();
}

export function validRegistration(input: string): boolean {
  return /^[A-Z0-9]{2,8}$/.test(tidyRegistration(input));
}

// -- The application's status, from `driver/me` -------------------------------

export interface CourierVerification {
  readonly status: 'PENDING' | 'APPROVED' | 'REJECTED' | string;
  readonly reviewer: 'PHARMACY' | 'PLATFORM' | string;
  readonly submittedAt?: string | null;
  readonly decidedAt?: string | null;
  readonly reason?: string | null;
}

export interface CourierStorefront {
  readonly id: string;
  readonly slug: string | null;
  readonly name: string | null;
}

export type CourierMe =
  | { readonly registered: false; readonly personId: string }
  | {
      readonly registered: true;
      readonly personId: string;
      readonly id: string;
      readonly displayName: string;
      readonly vehicle: string;
      readonly active: boolean;
      readonly canDeliver: boolean;
      readonly platformPool: boolean;
      readonly registrationNumber: string | null;
      readonly verification: CourierVerification | null;
      readonly storefronts: readonly CourierStorefront[];
    };

export interface StatusCopy {
  readonly tone: 'wait' | 'good' | 'bad';
  readonly badge: string;
  readonly title: string;
  readonly body: string;
}

/** Who decides the application, in words. Only ever what the platform recorded. */
function reviewerWords(me: Extract<CourierMe, { registered: true }>): string {
  if (me.verification?.reviewer === 'PHARMACY') {
    const name = me.storefronts.find((shop) => shop.name !== null)?.name;
    return name ?? 'the pharmacy you chose';
  }
  return 'Just Treated';
}

/**
 * What a courier who has applied is told, from the platform's own record.
 *
 * A courier the platform registered directly has no verification at all; the
 * platform counts that as approved, and so does this.
 */
export function statusCopy(me: Extract<CourierMe, { registered: true }>): StatusCopy {
  const status = me.verification?.status ?? 'APPROVED';
  if (status === 'PENDING') {
    return {
      tone: 'wait',
      badge: 'Waiting for review',
      title: 'Your application is with us',
      body: `${reviewerWords(me)} will review your documents. You'll find the decision here and in your Just Treated notifications.`,
    };
  }
  if (status === 'REJECTED') {
    const reason = me.verification?.reason;
    return {
      tone: 'bad',
      badge: 'Not approved',
      title: 'Your application was not approved',
      body:
        reason !== undefined && reason !== null && reason.trim() !== ''
          ? `The reason given: ${reason.trim()}`
          : 'No reason was recorded with the decision.',
    };
  }
  if (!me.active) {
    return {
      tone: 'bad',
      badge: 'Paused',
      title: 'Your courier account is paused',
      body: 'You will not be offered new deliveries while it is paused.',
    };
  }
  return {
    tone: 'good',
    badge: 'Approved',
    title: 'You can start delivering',
    body: 'Sign in to the JT Delivery app with this account to see deliveries waiting.',
  };
}

// -- Where to deliver, from `delivery/areas` ----------------------------------

export interface DeliveryArea {
  readonly area: string;
  readonly pharmacies: readonly { readonly id: string; readonly slug: string; readonly name: string }[];
  readonly distanceMiles?: number | null;
}

export interface PharmacyOption {
  readonly id: string;
  readonly label: string;
}

/** The pharmacies near the courier's postcode, nearest town first, each once. */
export function pharmacyOptions(areas: readonly DeliveryArea[], limit = 30): readonly PharmacyOption[] {
  const seen = new Set<string>();
  const options: PharmacyOption[] = [];
  for (const area of areas) {
    for (const shop of area.pharmacies) {
      if (seen.has(shop.id)) continue;
      seen.add(shop.id);
      const miles = typeof area.distanceMiles === 'number' ? ` · ${area.distanceMiles.toFixed(1)} mi` : '';
      options.push({ id: shop.id, label: `${shop.name} — ${area.area}${miles}` });
      if (options.length >= limit) return options;
    }
  }
  return options;
}
